import { useEffect, useRef } from 'react';
import { useIsPhone } from '../events/EventsPage';
import { DialogLayer } from '../ui/Dialog';

/** "Pobierz PDF": the server names the file (`kosztorys-<data>-<wydarzenie>-v<N>.pdf`); a sent version gives its stored PDF. */
export function downloadPdf(versionId: string) {
  downloadFrom(`/api/quotes/versions/${versionId}/pdf`);
}

/** Downloads the file of `url` under the name the server gives it. */
export function downloadFrom(url: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = '';
  document.body.append(a);
  a.click();
  a.remove();
}

/** The PDF of a version of „Oferta łączona” (wzór F, 2026-10-31). */
export const offerPdfUrl = (offerVersionId: string): string => `/api/quotes/offer-versions/${offerVersionId}/pdf`;

/**
 * "Podgląd PDF" (P8, owner points 8/26): the version's PDF in a window over the quote — a draft rendered now and
 * marked "PROJEKT", a sent version its stored file. Nothing is saved, sent or changed. On a phone the window fills
 * the screen. Safari on iPhone may show only the first page in a frame: "Otwórz w nowej karcie" shows all of it.
 * "Zamknij" takes the focus; closing ("Zamknij" or Escape, `DialogLayer`) gives focus back to what opened it (e.g.
 * "Podgląd PDF" in "Wyślij do klienta"), so a next Escape closes that dialog, not the event card.
 */
export function PdfPreviewDialog({ versionId, number, onDownload, onClose, offer = false }: { versionId: string; number: number; onDownload: () => void; onClose: () => void; /** „Oferta łączona” (2026-10-31): the offer version's PDF, no print view */ offer?: boolean }) {
  const phone = useIsPhone();
  const close = useRef<HTMLButtonElement>(null);
  const src = offer ? `${offerPdfUrl(versionId)}?inline=1` : `/api/quotes/versions/${versionId}/pdf?inline=1`;
  useEffect(() => close.current?.focus(), []);
  return (
    <div className="dialog-backdrop">
      <div className={`dialog panel q-pdf-preview${phone ? ' full' : ''}`} role="dialog" aria-modal="true" aria-labelledby="pdf-preview-title" data-feature-id="quotes.pdf_preview">
        <div className="q-pdf-preview-head">
          <h2 id="pdf-preview-title">Podgląd PDF · v{number}</h2>
          <div className="actions">
            <a className="btn small ghost" href={src} target="_blank" rel="noopener">
              Otwórz w nowej karcie
            </a>
            <button className="btn small" type="button" onClick={onDownload}>
              Pobierz PDF
            </button>
            {/* wzór F (owner 2026-10-30): the same version in the print view */}
            {!offer && (
              <a className="btn small ghost" href={`/wydruk/kosztorys/${versionId}`} data-feature-id="quotes.print_view">
                Podgląd wydruku
              </a>
            )}
            <DialogLayer onClose={onClose} />
            <button ref={close} className="btn small primary" type="button" onClick={onClose}>
              Zamknij
            </button>
          </div>
        </div>
        <iframe className="q-pdf-frame" src={src} title={`${offer ? 'PDF oferty łączonej' : 'PDF kosztorysu'}, wersja ${number}`} />
      </div>
    </div>
  );
}
